import { ElementRef, Inject, Injectable } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { WINDOW } from '@ng-web-apis/common';
import { ScrollDispatcher } from '@angular/cdk/overlay';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  Observable,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { isScullyRunning } from '@scullyio/ng-lib';
import { isEqual } from '@madeinlune/ngx-operators';

interface MediaState {
  isInViewport: boolean;
  loading: boolean;
  loaded: boolean;
  alt?: string;
  url?: string;
  size?: { width: number; height: number };
  target?: HTMLImageElement;
  forceLoading?: boolean;
  error?: boolean;
}

@Injectable()
export class MediaStore extends ComponentStore<MediaState> {
  set url(url: string | undefined) {
    this.patchState({ url });
  }

  set size(size: { width: number; height: number } | undefined) {
    this.patchState({ size });
  }

  set alt(alt: string | undefined) {
    this.patchState({ alt });
  }

  set target(target: HTMLImageElement | undefined) {
    this.patchState({ target });
  }

  set forceLoading(forceLoading: boolean | undefined) {
    this.patchState({ forceLoading });
  }

  readonly url$ = this.select((s) => s.url).pipe(distinctUntilChanged());

  readonly alt$ = this.select((s) => s.alt);

  readonly size$ = this.select((s) => s.size);

  readonly loading$ = this.select((s) => s.loading);

  readonly loaded$ = this.select((s) => s.loaded);

  readonly forceLoading$ = this.select((s) => s.forceLoading);

  isInViewport$ = this.select((s) => s.isInViewport);

  target$ = this.select((s) => s.target);

  setTarget = this.effect(
    (target$: Observable<HTMLImageElement | undefined>) => {
      return target$.pipe(
        filter((target) => !!target),
        tap((target) => {
          if (target) {
            this.patchState({ isInViewport: this.isInViewport() });
            this.checkTargetLoadEvent(fromEvent(target, 'load'));
            this.scrolling(
              this.scrollDispatcher.ancestorScrolled(this.el, 200)
            );
            this.updateSize();
            this.updateAlt();
          }
        })
      );
    }
  );

  setAlt = this.effect((alt$: Observable<string | undefined>) => {
    return alt$.pipe(
      tap(() => {
        this.updateAlt();
      })
    );
  });

  setUrl = this.effect((url$: Observable<string | undefined>) => {
    return url$.pipe(
      distinctUntilChanged(),
      tap((url) => {
        this.patchState({
          loading: false,
          loaded: false,
          isInViewport: this.isInViewport(),
        });
      })
    );
  });

  setSize = this.effect(
    (size$: Observable<{ width: number; height: number } | undefined>) => {
      return size$.pipe(
        tap((size) => {
          this.updateSize();
        })
      );
    }
  );

  checkTargetLoadEvent = this.effect((loadEvent$: Observable<any>) => {
    return loadEvent$.pipe(
      withLatestFrom(this.target$),
      filter(([event, target]) => !!target),
      tap(([event, target]) => {
        this.patchState({ loading: false, loaded: true });
        this.addLoadedClass();
      })
    );
  });

  scrolling = this.effect((scrolling$: Observable<any>) => {
    return scrolling$.pipe(
      withLatestFrom(this.loading$, this.loaded$),
      filter(([scrolling, loading, loaded]) => {
        const url = this.get((s) => s.url);
        return !loading && !loaded;
      }),
      tap(() => {
        this.patchState({ isInViewport: this.isInViewport() });
      })
    );
  });

  isInViewportAndNotLoaded = this.effect(
    (isInViewport$: Observable<boolean>) => {
      return combineLatest([
        isInViewport$,
        this.url$,
        this.loading$,
        this.loaded$,
        this.forceLoading$,
      ]).pipe(
        filter(([isInViewport, url, loading, loaded, forceLoading]) => {
          return (
            (isInViewport || !!forceLoading) && !loading && !loaded && !!url
          );
        }),
        isEqual(),
        switchMap(([isInViewport, url, loading, loaded]) => {
          if (url) {
            this.patchState({ loading: true, error: false });
            return this.httpClient.get(url, { responseType: 'blob' }).pipe(
              filter((blob) => !!blob),
              take(1),
              tapResponse(
                (blob) => {
                  this.patchState({ loaded: true });
                  const target = this.get((s) => s.target);
                  if (target) {
                    target.src = URL.createObjectURL(blob);
                  }
                },
                (error) => {
                  console.log({ error });
                  this.patchState({ loaded: false, error: true });
                }
              )
            );
          }
          return EMPTY;
        }),
        catchError((error) => {
          console.log({ error });
          return EMPTY;
        })
      );
    }
  );

  setLoading = this.effect((loading$: Observable<boolean>) => {
    return loading$.pipe(
      tap((loading) => {
        if (loading) {
          this.removeLoadedClass();
        }
      })
    );
  });

  constructor(
    private el: ElementRef<HTMLElement>,
    @Inject(WINDOW) private window: Window,
    private scrollDispatcher: ScrollDispatcher,
    private httpClient: HttpClient
  ) {
    super({
      loading: false,
      loaded: false,
      isInViewport: false,
      forceLoading: false,
    });
    if (isScullyRunning()) {
      this.addLoadedClass();
    }
    this.setTarget(this.target$);
    this.setLoading(this.loading$);
    this.setAlt(this.alt$);
    this.setUrl(this.url$);
    this.setSize(this.size$);
    this.isInViewportAndNotLoaded(this.isInViewport$);
  }

  private isInViewport() {
    const { nativeElement } = this.el;
    const rect = nativeElement.getBoundingClientRect();
    return (
      Math.round(rect.top) >= 0 &&
      Math.round(rect.left) >= 0 &&
      Math.round(rect.bottom) <= this.window.innerHeight * 2 &&
      Math.round(rect.right) <= this.window.innerWidth * 2
    );
  }

  addLoadedClass() {
    const { nativeElement } = this.el;
    nativeElement.classList.add('loaded');
  }

  removeLoadedClass() {
    const { nativeElement } = this.el;
    nativeElement.classList.remove('loaded');
  }

  private updateSize() {
    const target = this.get((s) => s.target);
    const size = this.get((s) => s.size);
    if (target && size) {
      target.setAttribute('width', `${size.width}`);
      target.setAttribute('height', `${size.height}`);
    }
  }

  private updateAlt() {
    const target = this.get((s) => s.target);
    const alt = this.get((s) => s.alt);
    if (target && alt) {
      target.setAttribute('alt', alt);
    }
  }
}
